// Sugalarm — shared inline-SVG action glyphs (cycle 106).
//
// Stroked, on `currentColor`, 24×24 viewBox at strokeWidth 1.8 to match
// the cycle-27.1 camera glyph (lifted here from PhotoMealScreen so there's
// exactly one camera in the codebase) and the rest of the app's stroked
// icon language. Each takes an optional `size` (px) + `className` so the
// same glyph serves a 22px trigger and an ~18px icon button.

interface IconProps {
  size?: number;
  className?: string;
}

function svgProps(size: number, className?: string) {
  return {
    viewBox: "0 0 24 24",
    width: size,
    height: size,
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true as const,
    className,
  };
}

/** Camera — lifted from the cycle-27.1 PhotoCarbEstimator glyph. */
export function CameraIcon({ size = 22, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <path d="M4 8h3l2-3h6l2 3h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z" />
      <circle cx="12" cy="13" r="3.5" />
    </svg>
  );
}

/** Trash can — lid + body + two inner strokes (Feather trash-2). */
export function TrashIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <path d="M3 6h18" />
      <path d="M19 6v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

/** Disconnect — the "ban"/no-entry glyph: a circle with a 45° chord
 *  through it (Feather slash). Stroke-only so it reads at 16–18px. */
export function DisconnectIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <circle cx="12" cy="12" r="9.25" />
      <path d="M5.46 5.46 18.54 18.54" />
    </svg>
  );
}

/** Photo — picture/landscape frame with a sun + a mountain (Feather
 *  image), distinct from the camera so "Choose image" reads as files. */
export function PhotoIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="8.5" cy="9" r="1.6" />
      <path d="M4 17l5-5 4 4 3-3 4 4" />
    </svg>
  );
}

/** Key — for "Update credentials" (Feather key). */
export function KeyIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <circle cx="7.5" cy="15.5" r="3.5" />
      <path d="M10 13 20 3M16.5 6.5 19 9M14 9l2 2" />
    </svg>
  );
}

/** Chevron-down — appended inside a tappable pill so it reads as a menu. */
export function ChevronDownIcon({ size = 14, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

/** Padlock — the privacy line under the onboarding provider step (cycle 475). */
export function LockIcon({ size = 14, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

/** Arrow-right — the trailing "go" mark on an onboarding choice row (cycle 475). */
export function ArrowRightIcon({ size = 14, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}
